import { LOCALE_NAMES } from "@repo/shared";
import type { LocaleCode, LocalizedString } from "@repo/shared/types";
import { ChevronDown, Languages } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { translateText } from "@/lib/api/ai";
import { useUserLanguage } from "@/lib/i18n/context";
import { getTranslationStatus, TRANSLATION_STATUS_COLORS } from "@/lib/i18n/translation-status";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./translation-button.i18n";

type TranslationButtonProps = {
  /** Current i18n value */
  value: LocalizedString;
  /** Called when translations are saved */
  onSave: (value: LocalizedString) => void | Promise<void>;
  /** Languages supported by tenant */
  supportedLanguages: string[];
  /** Optional: size of button (default: "icon") */
  size?: "sm" | "default" | "lg" | "icon";
  /** Optional: disabled state */
  disabled?: boolean;
};

export function TranslationButton({
  value,
  onSave,
  supportedLanguages,
  size = "icon",
  disabled = false,
}: TranslationButtonProps) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [localI18n, setLocalI18n] = useState<LocalizedString>({});
  const [isTranslating, setIsTranslating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Translation languages = all supported languages EXCEPT user's language
  const translationLangs = supportedLanguages.filter((lang) => lang !== userLanguage);
  const hasMultipleLanguages = translationLangs.length > 0;

  if (!hasMultipleLanguages) {
    return null;
  }

  const status = getTranslationStatus(value, translationLangs);
  const sourceValue = localI18n[userLanguage as LocaleCode] || "";

  // `localI18n` only holds anything between opening the dialog and closing it,
  // so reading it while the dialog is shut reports every language missing.
  // Anything derived from the draft goes through `draft`, never `localI18n`.
  const draft = dialogOpen ? localI18n : value;
  const missingLangs = translationLangs.filter((lang) => !draft[lang as LocaleCode]?.trim());
  const hasSomeTranslations = missingLangs.length < translationLangs.length;

  const handleOpen = () => {
    setLocalI18n({ ...value });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(localI18n);
      setDialogOpen(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAiTranslate = async (mode: "all" | "missing") => {
    if (!sourceValue?.trim()) return;

    const langsToTranslate = mode === "all" ? translationLangs : missingLangs;
    if (langsToTranslate.length === 0) return;

    setIsTranslating(true);
    try {
      const result = await translateText(sourceValue, userLanguage, langsToTranslate);
      if (result.success && result.translations) {
        setLocalI18n((prev) => ({
          ...prev,
          ...result.translations,
        }));
      }
    } finally {
      setIsTranslating(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size={size}
        className={cn(TRANSLATION_STATUS_COLORS[status])}
        onClick={handleOpen}
        disabled={disabled}
        tooltip={t.manageTranslations}
        // The button is glyph-only, and `Button` only derives a name from
        // `tooltip` at `size="icon"` — which the default is, but a caller can
        // pass any of the four. Radix wires the tooltip as `aria-describedby`,
        // never the name, so at the other three sizes this is the only thing
        // between the button and announcing as "button" and nothing else.
        aria-label={t.manageTranslations}
      >
        <Languages className="h-4 w-4" />
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t.translations}</DialogTitle>
            <DialogDescription>
              {t.enterTranslationsFor} &quot;{sourceValue}&quot;
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Primary language (read-only - edit inline in the table) */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {LOCALE_NAMES[userLanguage as LocaleCode] || userLanguage}
              </Label>
              <Input
                value={localI18n[userLanguage as LocaleCode] || ""}
                disabled
                className="bg-muted"
              />
            </div>

            {/* Other languages */}
            {translationLangs.map((lang) => (
              <div key={lang} className="space-y-2">
                <Label className="text-sm font-medium">
                  {LOCALE_NAMES[lang as LocaleCode] || lang}
                </Label>
                <Input
                  value={localI18n[lang as LocaleCode] || ""}
                  onChange={(e) => setLocalI18n((prev) => ({ ...prev, [lang]: e.target.value }))}
                  placeholder={t.translationPlaceholder}
                />
              </div>
            ))}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex gap-2 mr-auto">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" disabled={!sourceValue?.trim()} loading={isTranslating}>
                    {t.aiTranslate}
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {hasSomeTranslations && missingLangs.length > 0 && (
                    <DropdownMenuItem onClick={() => handleAiTranslate("missing")}>
                      {t.translateMissing} ({missingLangs.length})
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => handleAiTranslate("all")}>
                    {t.translateAll} ({translationLangs.length})
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <DialogCancel disabled={isSaving}>{t.cancel}</DialogCancel>
            <Button onClick={handleSave} disabled={isSaving}>
              {t.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
